import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import logo from '@/assets/logo.png';

const navLinks = [
  { label: 'Services', href: '/#services' },
  { label: 'Public Audits', href: 'https://github.com/BugBlow/audits/', external: true },
  
  { label: 'FAQ', href: '/#faq' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contacts', href: '/#contacts' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const hash = href.slice(1); // e.g. "#services"
      if (location.pathname === '/') {
        // Already on index, just scroll
        const el = document.querySelector(hash);
        el?.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Navigate to index, then scroll
        navigate('/' + hash);
      }
      setMobileMenuOpen(false);
    }
  }, [location.pathname, navigate]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border/30">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - larger size */}
          <a href="/" className="flex items-center flex-shrink-0">
            <img src={logo} alt="BugBlow" className="h-16 lg:h-20 w-auto" width="204" height="80" />
          </a>

          {/* Desktop Navigation - Centered */}
          <nav aria-label="Main navigation" className="hidden lg:flex items-center justify-center flex-1">
            <div className="flex items-center gap-10">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-xl text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </nav>

          {/* Request a Quote button */}
          <div className="hidden lg:block">
            <Button
              className="btn-primary-filled rounded-full px-6"
              onClick={() => {
                if (location.pathname === '/') {
                  const el = document.querySelector('#contacts');
                  el?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate('/#contacts');
                }
              }}
            >
              Request a Quote
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-foreground"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav aria-label="Mobile navigation" className="lg:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
              <Button
                className="btn-primary-filled rounded-full w-full mt-2"
                onClick={() => {
                  setMobileMenuOpen(false);
                  if (location.pathname === '/') {
                    const el = document.querySelector('#contacts');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    navigate('/#contacts');
                  }
                }}
              >
                Request a Quote
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
